import React, { ChangeEventHandler, useCallback, useEffect, useMemo, useState } from 'react'
import { rem } from 'polished'
import { connect } from 'react-redux'
import styled from 'styled-components'
import * as Analytics from 'analytics/analytics'
import { useAppDispatch } from 'hooks/reduxHooks'
import { saveUserPreferences } from 'actions/RecommendationActions'
import Group from 'components/utils/Group'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'components/Luxkit/Typography/BodyText'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import LineSearchIcon from 'components/Luxkit/Icons/line/LineSearchIcon'
import LineCheckCircleIcon from 'components/Luxkit/Icons/line/LineCheckCircleIcon'
import { TRAVEL_PREFERENCES_DESTINATIONS, ModalSteps, MAX_SELECTIONS } from '../defaults'
import TravelPreferencesImageItemsGrid from '../TravelPreferencesImageItemsGrid'

const StyledSearchInput = styled(TextInputOnly)`
  ${mediaQueryUp.tablet} {
    max-width: ${rem(396)};
  }
`

interface Props {
  userPreferences?: App.UserPreferences;
  onSetNextStep: (step: ModalSteps, isSaveAction?: boolean) => void;
}

function TravelPreferencesModalDestinations(props: Props) {
  const { userPreferences, onSetNextStep } = props

  const dispatch = useAppDispatch()
  const [destinationSearch, setDestinationSearch] = useState<string>('')
  const [selectedDestinations, setSelectedDestinations] = useState<Array<App.UserPreferencesDestination>>(userPreferences?.destinations || [])
  const maxDestinationsSelected = selectedDestinations.length === MAX_SELECTIONS

  const isSelected = useCallback((destination: App.UserPreferencesDestination) => !!selectedDestinations.find(item => item.placeId === destination.placeId), [selectedDestinations])

  const filteredDestinations = useMemo(() => {
    if (!destinationSearch) return TRAVEL_PREFERENCES_DESTINATIONS
    return TRAVEL_PREFERENCES_DESTINATIONS.filter(item => item.destination.name.toLowerCase().includes(destinationSearch.toLowerCase()))
  }, [destinationSearch])

  const onToggleSelection = useCallback((destination: App.UserPreferencesDestination) => {
    const selected = isSelected(destination)
    if (!selected && maxDestinationsSelected) return
    setSelectedDestinations(prevSelected => {
      if (selected) {
        return prevSelected.filter(item => item.placeId !== destination.placeId)
      }

      return [...prevSelected, destination]
    })
  }, [isSelected, maxDestinationsSelected])

  const handleSearchDestination = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setDestinationSearch(e.currentTarget.value)
  }, [])

  const onSkip = useCallback(() => {
    onSetNextStep(ModalSteps.hotelBrands)
  }, [onSetNextStep])

  const onUpdatePreferences = useCallback(() => {
    const updatedPreferences = {
      holidayTypes: userPreferences?.holidayTypes || [],
      hotelBrands: userPreferences?.hotelBrands || [],
      occupancy: userPreferences?.occupancy || [],
      groupSize: userPreferences?.groupSize,
      destinations: selectedDestinations,
    }

    Analytics.trackClientEvent({
      subject: 'destinations',
      action: 'completion',
      type: 'interaction',
      category: 'travel_preferences',
    })

    dispatch(saveUserPreferences(updatedPreferences))
    onSetNextStep(ModalSteps.hotelBrands, true)
  }, [dispatch, onSetNextStep, selectedDestinations, userPreferences])

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'destinations',
      action: 'impression',
      type: 'nonInteraction',
      category: 'travel_preferences',
    })
  }, [])

  return (
    <>
      <ModalBody>
        <ModalContent>
          <Group direction="vertical" gap={16}>
            <Group direction="vertical" gap={8}>
              <BodyText variant="medium">Be the first to know about the best offers in these locations. Select up to five destinations.</BodyText>
              <BodyTextBlock
                variant="medium"
                colour={maxDestinationsSelected ? 'highlight-secondary' : 'neutral-three'}
                startIcon={maxDestinationsSelected && <LineCheckCircleIcon colour="highlight-secondary" />}
              >
                {selectedDestinations.length} of {MAX_SELECTIONS} selected
              </BodyTextBlock>
            </Group>
            <StyledSearchInput
              type="text"
              value={destinationSearch}
              placeholder="Search destination"
              onChange={handleSearchDestination}
              endIcon={<LineSearchIcon />}
              noValidationSpacing
            />
            <TravelPreferencesImageItemsGrid
              items={filteredDestinations}
              isSelected={isSelected}
              onSelect={onToggleSelection}
            />
          </Group>
        </ModalContent>
      </ModalBody>
      <ModalFooter primaryActionProps={{ children: 'Save & continue', onClick: onUpdatePreferences }} secondaryActionProps={{ children: 'Skip', onClick: onSkip }} />
    </>
  )
}

const mapStateToProps = (state: App.State) => ({
  userPreferences: state.recommendations.userPreferences.preferences,
})

export default connect(mapStateToProps)(TravelPreferencesModalDestinations)
